import {
  styled,
  Card,
  Typography,
  Stack,
  Box,
  IconButton,
  MenuItem,
  ListItemIcon,
  Link,
  Tooltip,
  Collapse,
} from "@mui/material";
import * as React from "react";
import useCountMaxTags from "../../../Utils/Hooks/useCountMaxTags";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { IconButtonProps } from "@mui/material/IconButton";
import MoreMenu from "../../Misc/MoreMenu";
import { userService } from "../../../Core/Api/services";
import { DbSearchAdvancedQueryInfos } from "../../../Core/Api/Types/db-search-advanced-query-infos";
import AdvancedQueries, { AdvancedQueriesProps } from "../AdvancedQueries/advanced-queries";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { convertStringToAdvancedQuery } from "../../../Pages/admin/Inventory/InventoryIndex/InventoryAdvancedQueries/inventory-advanced-queries";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ModalSendQuery, { ModalSendQueryElementProps } from "../../Modals/ModalSendQuery/modal-send-query";
import { ModalContext } from "../../Modals";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

export interface AdvancedQuerySavedProps {
  title: string;
  pattern: string;
  query: DbSearchAdvancedQueryInfos;
  id: number;
  queries: DbSearchAdvancedQueryInfos[];
  removeQuery: React.Dispatch<React.SetStateAction<DbSearchAdvancedQueryInfos[]>>;
  onClickSearch: (query: string, layer: ObjectLayer, title: string) => void;
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const AdvancedQuerySaved: React.FC<AdvancedQuerySavedProps> = (props) => {
  const { t } = useTranslation();
  const { title, query, id, onClickSearch, removeQuery, queries, pattern, ...otherProps } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [queryAdvanced, setQueryAdvanced] = React.useState<AdvancedQueriesProps["queries"]>([]);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const { visibleTags, hiddenTagsCount } = useCountMaxTags({ tagList: queryAdvanced, nbMax: 5 });

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const onClickDelete = () => {
    userService.DeleteSearchQuery(id);
    removeQuery(queries.filter((x) => x.id !== id));
  };

  const onClickSendTo = () => {
    if (!openModal) return;

    openModal<ModalSendQueryElementProps>(ModalSendQuery, {
      query: query,
    });
  };

  const onClickCopyToClipBoard = () => {
    if (query.query) {
      navigator.clipboard.writeText(query.query);
      enqueueCustomSnackbar(t("alert:primary.queryCopiedToClipboard"), "primary");
    }
  };

  React.useEffect(() => {
    if (query.query && query.layer) setQueryAdvanced(convertStringToAdvancedQuery(query.query, query.layer));
  }, []);

  return (
    <StyledCard variant="outlined" {...otherProps}>
      <Stack flex={0.6}>
        <Typography fontWeight={500} mb={1}>
          {title} ({pattern})
        </Typography>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box>
            <AdvancedQueries queries={queryAdvanced} />
            <Tooltip
              arrow
              PopperProps={{ sx: { ".MuiTooltip-tooltip": { maxWidth: 315, p: 1, pb: 0.5 } } }}
              title={
                <Box>
                  <AdvancedQueries
                    queries={[
                      { start: "Type", condition: "is", end: "Asset", color: "success" },
                      { operator: "and" },
                      { start: "Type", condition: "is", end: "Asset", color: "success" },
                      { operator: "or" },
                      { start: "Type", condition: "is not", end: "Asset", color: "error" },
                      { operator: "or" },
                      { start: "Type", condition: "is not", end: "Asset", color: "error" },
                      { operator: "and" },
                      { start: "Type", condition: "is not", end: "Asset", color: "error" },
                    ]}
                  />
                </Box>
              }
            >
              <>
                {hiddenTagsCount > 0 && (
                  <Link color="primary.text" underline="hover">
                    + {hiddenTagsCount}
                  </Link>
                )}
              </>
            </Tooltip>
          </Box>
        </Collapse>
      </Stack>
      <Stack direction="row" alignItems={"center"} justifyContent={"flex-end"} flex={0.4}>
        <IconButton onClick={() => onClickSearch(query.query!, query.layer!, query.name!)}>
          <SearchIcon fontSize="small" />
        </IconButton>
        <MoreMenu size="small">
          <MenuItem onClick={onClickCopyToClipBoard}>
            <ListItemIcon>
              <CopyAllOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t("menu:item.copyToClipboard")}
          </MenuItem>
          <MenuItem onClick={onClickSendTo}>
            <ListItemIcon>
              <SendOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t("menu:item.sendTo")}
          </MenuItem>
          <MenuItem color="error" onClick={onClickDelete}>
            <ListItemIcon>
              <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography color="error">{t("common:delete")}</Typography>
          </MenuItem>
        </MoreMenu>
        <ExpandMore expand={expanded} onClick={() => setExpanded(!expanded)} aria-expanded={expanded} sx={{ ml: 0 }}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Stack>
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: theme.spacing(1.5),
  background: theme.palette.background.paper,
}));

export default AdvancedQuerySaved;
